import type { Config} from './environment';
import config, { getEnvironment } from './environment';

/**
 * Validates the configuration and throws errors for missing required fields
 */
export const validateConfig = (configToValidate: Config): Config => {
  const currentEnv = getEnvironment();

  // Skip validation in development
  if (currentEnv === 'development') {
    return configToValidate;
  }

  const requiredFields: (keyof Config)[] = ['apiUrl', 'socketUrl', 'stripeKey'];
  const requiredAuthFields: (keyof Config['auth0'])[] = ['domain', 'clientId'];

  const missingFields = requiredFields.filter(
    field => !configToValidate[field],
  );
  const missingAuthFields = requiredAuthFields.filter(
    field => !configToValidate.auth0[field],
  );

  if (missingFields.length > 0 || missingAuthFields.length > 0) {
    const envVarNames: string[] = [];

    // Map missing fields to their environment variable names
    if (missingFields.includes('apiUrl')) envVarNames.push('VITE_API_URL');
    if (missingFields.includes('socketUrl')) envVarNames.push('VITE_SOCKET_URL');
    if (missingFields.includes('stripeKey')) {
      envVarNames.push(currentEnv === 'production' ? 'VITE_STRIPE_KEY_PRODUCTION' : 'VITE_STRIPE_KEY_TEST');
    }
    if (missingAuthFields.includes('domain')) envVarNames.push('VITE_AUTH0_DOMAIN');
    if (missingAuthFields.includes('clientId')) envVarNames.push('VITE_AUTH0_CLIENT_ID');

    const errorMsg = `[CONFIG ERROR] Missing required environment variables for ${currentEnv}:\n${envVarNames.join(', ')}`;

    console.error(errorMsg);
    console.error('Current config:', {
      ...configToValidate,
      stripeKey: configToValidate.stripeKey ? '[HIDDEN]' : 'undefined',
    });
  }

  return configToValidate;
};

/**
 * Get validated configuration for the current environment
 */
export const getValidatedConfig = (): Config => {
  const currentConfig = validateConfig(config[getEnvironment()]);

  // Add debugging information in development mode (not during tests)
  if (import.meta.env.DEV && !import.meta.env.VITEST) {
    console.debug('Current Environment:', getEnvironment());
    console.debug('Current Config:', {
      ...currentConfig,
      // Mask sensitive values
      stripeKey: currentConfig.stripeKey ? '[SET]' : '[NOT SET]',
      auth0: {
        domain: currentConfig.auth0.domain ? '[SET]' : '[NOT SET]',
        clientId: currentConfig.auth0.clientId ? '[SET]' : '[NOT SET]',
        redirectUri: currentConfig.auth0.redirectUri || '[Runtime]',
      },
    });
  }

  return currentConfig;
};
